/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import {
	type KeyboardEvent,
	useCallback,
	useEffect,
	useId,
	useMemo,
	useRef,
	useState,
} from 'react';

import isEqual from 'lodash/isEqual';
import { useIntl } from 'react-intl';

import { type DocNode } from '@atlaskit/adf-schema';
import Button from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import { Box, Inline, Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { RovoChatPromptInput } from '@atlassian/conversation-assistant-chat-prompt-input/RovoChatPromptInput';
import { encodeAdf } from '@atlassian/conversation-assistant-content-transformer/adf-encoder';
import { setDraftMessage } from '@atlassian/conversation-assistant-store/controllers/drafts';

import { useGlobalConfig } from '../../../../common/utils/global-config/useGlobalConfig';
import { useAssistanceServiceParams } from '../../../../common/utils/use-assistance-service-params';
import { DEFAULT_AGENT } from '../../../../controllers/agents/constants';
import { useConversation } from '../../../../controllers/conversation/useConversation';
import { extractText } from '../extract-text';

import messages from './messages';

const NOOP = (): void => {};

const styles = cssMap({
	wrapper: {
		width: '100%',
		backgroundColor: token('elevation.surface.sunken'),
		borderRadius: token('radius.xlarge'),
		paddingTop: token('space.100'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.100'),
	},
	editorContainer: {
		// @ts-expect-error - nested selector not supported in cssMap strict types
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& [data-testid="chat-input-wrapper"]': {
			boxShadow: 'none',
			minHeight: 'auto',
			borderRadius: token('radius.large'),
			backgroundColor: token('color.background.input'),
			paddingInline: token('space.100'),
			paddingTop: token('space.100'),
			paddingBottom: token('space.0'),
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& [data-testid="chat-input-wrapper"]:focus-within': {
			border: `${token('border.width')} solid ${token('color.border.focused')}`,
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& .ak-editor-content-area': {
			paddingTop: token('space.0'),
			paddingRight: token('space.0'),
			paddingLeft: token('space.0'),
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& .ProseMirror': {
			minHeight: '24px',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& [data-testid="chat-prompt-footer-row"]': {
			display: 'none',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& [data-testid="chat-input-wrapper-content"]': {
			paddingInline: token('space.0'),
			paddingBlock: token('space.0'),
		},
	},
	updatePromptHint: {
		font: token('font.body.small'),
		color: token('color.text.subtlest'),
	},
	actionRow: {
		paddingInlineStart: token('space.100'),
	},
	updateButtonRadius: {
		// @ts-expect-error - nested selector not supported in cssMap strict types
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& button': {
			borderRadius: token('radius.medium'),
		},
	},
});

export type InlineEditPromptEditorProps = {
	conversationId: string;
	initialContent: DocNode | string;
	/** Called when the user clicks Cancel or presses Escape. */
	onCancel: () => void;
	/** Called with the new prompt when the user submits. */
	onUpdate: (newPrompt: DocNode) => void;
};

export const InlineEditPromptEditor = ({
	conversationId,
	initialContent,
	onCancel,
	onUpdate,
}: InlineEditPromptEditorProps): JSX.Element => {
	const { formatMessage } = useIntl();
	const { productKey, siteId, orgId } = useGlobalConfig();
	const { selectedAgent } = useConversation({ conversationId });
	const assistanceServiceParams = useAssistanceServiceParams({ touchpointSource: 'chatInput' });

	// Normalize the prop once: callers may pass either an ADF DocNode (the
	// common case — content captured by the composer) or a plain string
	// (legacy / server-restored history).
	const initialPrompt = useMemo(() => encodeAdf(initialContent), [initialContent]);

	const [currentPrompt, setCurrentPrompt] = useState<DocNode | null>(initialPrompt);

	const isPromptSubmittable = useCallback(
		(prompt: DocNode | null): boolean => {
			if (!prompt) {
				return false;
			}
			if (extractText(prompt).trim().length === 0) {
				return false;
			}
			return !isEqual(prompt, initialPrompt);
		},
		[initialPrompt],
	);

	const isSubmitDisabled = useMemo(
		() => !isPromptSubmittable(currentPrompt),
		[currentPrompt, isPromptSubmittable],
	);

	const sessionId = useId();
	const promptDraftAndModeKey = `${conversationId}-inline-edit-${sessionId}`;

	const hasSeededRef = useRef(false);
	if (!hasSeededRef.current) {
		hasSeededRef.current = true;
		setDraftMessage(promptDraftAndModeKey, initialPrompt);
	}

	// Clear our entry on unmount; `promptDraftAndModeKey` is unique per edit
	// session (`useId`), so without cleanup the drafts store would leak.
	useEffect(() => {
		return () => setDraftMessage(promptDraftAndModeKey, null);
	}, [promptDraftAndModeKey]);

	const handleSubmit = useCallback(
		(prompt: DocNode) => {
			if (isPromptSubmittable(prompt)) {
				onUpdate(prompt);
			}
		},
		[isPromptSubmittable, onUpdate],
	);

	const handleUpdateClick = useCallback(() => {
		if (currentPrompt && isPromptSubmittable(currentPrompt)) {
			onUpdate(currentPrompt);
		}
	}, [currentPrompt, isPromptSubmittable, onUpdate]);

	const handleWrapperKeyDown = useCallback(
		(event: KeyboardEvent<HTMLDivElement>) => {
			if (event.key === 'Escape') {
				event.stopPropagation();
				onCancel();
			}
		},
		[onCancel],
	);

	const isConversationAgentic =
		!!selectedAgent && selectedAgent.named_id !== DEFAULT_AGENT.named_id;

	return (
		<Box xcss={styles.wrapper} onKeyDown={handleWrapperKeyDown}>
			<Stack space="space.100">
				<Box xcss={styles.editorContainer}>
					<RovoChatPromptInput
						productKey={productKey}
						siteId={siteId}
						orgId={orgId}
						assistanceServiceParams={assistanceServiceParams}
						arrowKeyPluginConfigMessages={[]}
						promptDraftAndModeKey={promptDraftAndModeKey}
						isConversationAgentic={isConversationAgentic}
						isAgentTyping={false}
						onPromptChange={setCurrentPrompt}
						onSubmit={handleSubmit}
						onAbort={NOOP}
						showActionsButton={false}
						showChatModeMenu={false}
						showConnectorTip={false}
						useCurrentPageContext={false}
						enableBrowserContextBanner={false}
						isSpaceScopedConversation={false}
						enableVoiceDictation={false}
						disableWrapperMarginStyles
						minHeight={24}
					/>
				</Box>

				<Inline
					alignBlock="center"
					spread="space-between"
					space="space.150"
					xcss={styles.actionRow}
				>
					<Box xcss={styles.updatePromptHint}>{formatMessage(messages.updatePromptHint)}</Box>
					<Inline alignBlock="center" space="space.100">
						<Button appearance="subtle" onClick={onCancel} type="button">
							{formatMessage(messages.cancelLabel)}
						</Button>
						<Box xcss={styles.updateButtonRadius}>
							<Button
								appearance="primary"
								onClick={handleUpdateClick}
								isDisabled={isSubmitDisabled}
								type="button"
							>
								{formatMessage(messages.updateLabel)}
							</Button>
						</Box>
					</Inline>
				</Inline>
			</Stack>
		</Box>
	);
};
